import React, { useRef, useState } from 'react';
import './line-split-input.scss';

// What blocks Apply, as i18n keys: the matter split's three rules (validate-form.js),
// plus "every row is at least 1", which shares the sum message (design §5.4).
const rowErrors = (rows, splitField) => {
  const personIds = rows.map((row) => splitField.valueOf(row.option));
  const errors = [];
  if (personIds.some((id) => id === null)) {
    errors.push('invoice.line_split.error_all_required');
  }
  const namedIds = personIds.filter((id) => id !== null);
  if (new Set(namedIds).size !== namedIds.length) {
    errors.push('invoice.line_split.error_duplicate');
  }
  const everyRowAtLeastOne = rows.every((row) => Number.isInteger(row.percentage) && row.percentage >= 1);
  const total = rows.reduce((sum, row) => sum + (Number.isInteger(row.percentage) ? row.percentage : 0), 0);
  if (!everyRowAtLeastOne || total !== 100) {
    errors.push('invoice.line_split.error_sum');
  }
  return errors;
};

/**
 * XPEN-3565: Single / Multiple control for splitting one invoice line between people.
 *
 * Single renders the ERP's existing picker unchanged. Multiple collects rows of
 * person + whole-number percentage and hands them to `onApplySplit`; the control
 * itself saves nothing. A row's picker only updates that row: it never receives
 * `onSingleChange`, because today's pickers save the line on change.
 *
 * @param {object} props
 * @param {object} props.data - The MobX store, for `data.t.read`.
 * @param {object|null} props.splitField - The line's split settings (`getLineSplitField`).
 * @param {Function} props.renderPicker - `(value, onChange, options) => node`, the ERP's
 *   picker. The node must save nothing by itself (only through the `onChange` it is given)
 *   and must apply the field's own lock. Row pickers also get `{ isDisabled }`, true while
 *   the rows are locked, and their picks are ignored then; the Single picker is called
 *   with `(value, onSingleChange)` only.
 * @param {*} props.value - The line's current single value.
 * @param {Function} props.onSingleChange - Today's single-value handler.
 * @param {Function} [props.onApplySplit] - Receives `[{ option, percentage }]` on Apply.
 * @param {boolean} [props.isDisabled] - Locks the tabs, percentage inputs and buttons
 *   (row pickers take their lock from `renderPicker`).
 * @param {string|null} [props.splitDisabledReason] - Disables Multiple and says why; if it
 *   arrives while Multiple is open, the rows can no longer be changed or applied.
 * @param {string|null} [props.splitNotice] - A warning shown while Multiple is active.
 * @returns {React.ReactNode} The control, or only the picker when splitting is not offered.
 */
const LineSplitInput = ({
  data,
  splitField,
  renderPicker,
  value,
  onSingleChange,
  onApplySplit,
  isDisabled = false,
  splitDisabledReason = null,
  splitNotice = null,
}) => {
  // null while Single is active; the rows while Multiple is active.
  const [rows, setRows] = useState(null);
  const [applyAttempted, setApplyAttempted] = useState(false);
  // Stable React keys: rows can be removed, so the array index would shift.
  const nextRowId = useRef(0);

  if (!onApplySplit || !splitField) {
    return renderPicker(value, onSingleChange);
  }

  const labels = splitField.labels;
  const isMultiple = rows !== null;
  // Row controls and Apply: locked by the caller, or by a reason that arrived mid-edit.
  const rowsLocked = isDisabled || Boolean(splitDisabledReason);
  const newRow = (option, percentage) => {
    nextRowId.current += 1;
    return { id: nextRowId.current, option, percentage };
  };
  const updateRow = (id, patch) => setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const switchToMultiple = () => {
    if (!isMultiple) {
      setRows([newRow(value ?? null, 50), newRow(null, 50)]);
      setApplyAttempted(false);
    }
  };
  // Unlike the matter split, never hand row 1 to onSingleChange: that would save the line.
  const switchToSingle = () => setRows(null);

  const errors = isMultiple ? rowErrors(rows, splitField) : [];
  const apply = () => {
    setApplyAttempted(true);
    if (errors.length > 0) return;
    onApplySplit(rows.map(({ option, percentage }) => ({ option, percentage })));
    setRows(null);
  };

  return (
    <div className='line-split-input'>
      <div className='line-split-tabs' role='tablist'>
        <button
          type='button'
          role='tab'
          aria-selected={!isMultiple}
          className={`line-split-tab${!isMultiple ? ' active' : ''}`}
          disabled={isDisabled}
          onClick={switchToSingle}
        >
          {data.t.read(labels.single)}
        </button>
        <button
          type='button'
          role='tab'
          aria-selected={isMultiple}
          className={`line-split-tab${isMultiple ? ' active' : ''}`}
          disabled={isDisabled || Boolean(splitDisabledReason)}
          onClick={switchToMultiple}
        >
          {data.t.read(labels.multiple)}
        </button>
      </div>
      {splitDisabledReason && <div className='text-muted small mb-2'>{splitDisabledReason}</div>}

      {!isMultiple && renderPicker(value, onSingleChange)}

      {isMultiple && (
        <div className='line-split-rows'>
          {rows.map((row) => (
            <div key={row.id} className='line-split-row'>
              <div className='line-split-select'>
                {renderPicker(
                  row.option,
                  (choice) => {
                    if (!rowsLocked) updateRow(row.id, { option: choice ?? null });
                  },
                  { isDisabled: rowsLocked },
                )}
              </div>
              <div className='input-group line-split-percentage'>
                <input
                  type='number'
                  className='form-control'
                  value={row.percentage}
                  min={1}
                  max={100}
                  step={1}
                  disabled={rowsLocked}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value, 10);
                    updateRow(row.id, { percentage: Number.isNaN(parsed) ? '' : parsed });
                  }}
                  aria-label={data.t.read(labels.percentage)}
                />
                <div className='input-group-append'>
                  <span className='input-group-text'>%</span>
                </div>
              </div>
              {rows.length > 2 && (
                <button
                  type='button'
                  className='line-split-remove'
                  aria-label={data.t.read(labels.remove)}
                  disabled={rowsLocked}
                  onClick={() => setRows((current) => current.filter((item) => item.id !== row.id))}
                >
                  &times;
                </button>
              )}
            </div>
          ))}
          <button
            type='button'
            className='line-split-add'
            disabled={rowsLocked}
            onClick={() => {
              const row = newRow(null, 0);
              setRows((current) => [...current, row]);
            }}
          >
            {data.t.read(labels.add)}
          </button>
          {splitNotice && <div className='alert alert-warning small mt-2 mb-0'>{splitNotice}</div>}
          {applyAttempted && errors.map((key) => (
            <div key={key} className='text-danger small mt-1'>{data.t.read(key)}</div>
          ))}
          <div className='line-split-apply'>
            <button type='button' className='btn btn-primary btn-sm' disabled={rowsLocked} onClick={apply}>
              {data.t.read('invoice.line_split.apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LineSplitInput;
